/**
 * Data adapter functions to transform between Supabase schema and component interfaces
 */

import { Database } from "@/integrations/supabase/types";
import { CreditCard } from "@/types/card";

type DbCard = Database['public']['Tables']['cards']['Row'];

/**
 * Transforms a Supabase card record into the CreditCard interface
 * used by the CompareDialog component
 */
export function adaptSupabaseCardToCompareCard(
  dbCard: DbCard,
  isSponsored: boolean = false
): CreditCard {
  // Format credit limit range
  const creditLimit = dbCard.credit_limit_min && dbCard.credit_limit_max
    ? `${dbCard.credit_limit_min.toLocaleString('nb-NO')} - ${dbCard.credit_limit_max.toLocaleString('nb-NO')} kr`
    : 'Ikke oppgitt';

   // Generate badges based on card properties
   const badges: string[] = [];
   if (isSponsored) badges.push('Anzeige');
   if (dbCard.annual_fee === 0) badges.push('Ingen gebyr');
  if (dbCard.travel_insurance) badges.push('Reise');
  if ((dbCard.boost_priority ?? 0) > 5) badges.push('Anbefalt');

  // Extract features from perks if available, otherwise use benefits
  const features = dbCard.perks && Array.isArray(dbCard.perks) 
    ? (dbCard.perks as string[])
    : (dbCard.benefits || []);

  return {
    id: dbCard.id,
    slug: dbCard.slug,
    name: dbCard.name,
    bank: dbCard.issuer,
    logo: dbCard.logo_url || 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=100&h=60&fit=crop',
    effectiveRate: dbCard.apr_effective || 0,
    annualFee: dbCard.annual_fee || 0,
    interestFreePeriod: dbCard.interest_free_days || 0,
    network: dbCard.network || 'Visa',
    creditLimit,
    minAge: dbCard.age_min || 18,
    minIncome: dbCard.income_min || undefined,
    features,
    badges,
    isSponsored,
    pros: dbCard.pros || [],
    cons: dbCard.cons || [],
    suitableFor: dbCard.best_for || [],
    lastUpdated: new Date(dbCard.updated_at || dbCard.created_at || Date.now()).toISOString().split('T')[0],
    nominalRate: dbCard.apr_nominal || 0,
    travelInsurance: dbCard.travel_insurance || false,
    sellingPoints: (dbCard as any).selling_points || dbCard.benefits?.slice(0, 3) || [],
    articleContent: undefined,
  };
}
